import {
	useField,
	useInputControl,
	getSelectProps,
	type FieldName,
} from '@conform-to/react'
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp'
import React, { useCallback, useId, useState } from 'react'
import { Checkbox, type CheckboxProps } from './ui/checkbox'
import { Input } from './ui/input'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp'
import { Label } from './ui/label'
import MultipleSelector, { type MultipleSelectorProps } from './ui/multi-select'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	type SelectProps,
	type OptionType,
} from './ui/select'
import { Textarea } from './ui/textarea'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map(e => (
				<li key={e} className="text-[10px] text-foreground-destructive">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	auxiliaryProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	auxiliaryProps?: React.HTMLAttributes<HTMLDivElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />{' '}
			{auxiliaryProps ? <>{auxiliaryProps.children}</> : null}
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			<div className="min-h-[32px] pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			<div className="min-h-[32px] pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					{...buttonProps}
					onCheckedChange={state => {
						input.change(state.valueOf() ? checkedValue : '')

						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={event => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={event => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="self-center text-body-xs"
				/>
			</div>
			<div className="pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export interface MultiSelectFieldProps<Name = FieldName<string | string[]>>
	extends Pick<MultipleSelectorProps, 'options'> {
	name: Name
	label: string
	placeholder?: string
	disabled?: boolean
	className?: string
	onValueChange?: MultipleSelectorProps['onChange']
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	selectAll?: boolean
}

export function MultiSelectField({
	labelProps,
	name,
	label,
	options,
	className,
	onValueChange,
	selectAll,
	disabled,
	...props
}: MultiSelectFieldProps) {
	// console.log({options});

	const fallbackId = useId()
	const id = fallbackId
	//
	const [meta, form] = useField(name)
	const [, setSearchTerm] = useState('')

	const value =
		typeof meta.value === 'string'
			? [meta.value]
			: meta.value?.filter(Boolean) ?? []

	const defaultSelected = options?.filter(option =>
		value.includes(option.value),
	)

	const allSelected =
		!!options?.length && options.every(option => value.includes(option.value))

	const handleToggleAll = () => {
		const newOptions = allSelected ? [] : options ?? []
		form.update({
			name: meta.name,
			value: newOptions.map(opt => opt.value),
		})
		onValueChange?.(newOptions)
	}

	const handleSearch = useCallback(
		async (searchText: string) => {
			// Update search term state (optional, if you want to use it elsewhere)
			setSearchTerm(searchText)

			// Filter options based on label
			return (options ?? []).filter(option =>
				option.label.toLowerCase().includes(searchText.toLowerCase()),
			)
		},
		[options],
	)

	const shadowSelectProps = {
		...getSelectProps(meta),
		defaultValue: undefined,
		value: value,
		onChange: () => {},
	}

	return (
		<div className={className}>
			<div className="flex items-center justify-between">
				<Label htmlFor={id} {...labelProps} />
				{selectAll && !disabled && options?.length ? (
					<button
						type="button"
						onClick={handleToggleAll}
						className="text-body-xs font-medium text-primary hover:underline"
					>
						{allSelected ? 'Tout retirer' : 'Tout sélectionner'}
					</button>
				) : null}
			</div>
			<select {...shadowSelectProps} key={shadowSelectProps.key} hidden>
				{value.map(v => (
					<option key={v} value={v} />
				))}
			</select>
			<MultipleSelector

				value={defaultSelected}
				onSearch={handleSearch}
				defaultOptions={options}
				onChange={selectedOptions => {
					form.update({
						name: meta.name,
						value: selectedOptions.map(opt => opt.value),
					})
					onValueChange?.(selectedOptions)
				}}
				options={options}
				disabled={disabled}
				{...props}
				emptyIndicator={
					<p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
						Aucun résultat.
					</p>
				}
			/>
		</div>
	)
}

export function SelectField({
	labelProps,
	buttonProps,
	errors,
	className,
	options,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	buttonProps: SelectProps & {
		id?: string
		key?: string
		name: string
		form: string
		value?: string
		placeholder?: string
		onFocus?: React.FocusEventHandler<HTMLButtonElement>
		onBlur?: React.FocusEventHandler<HTMLButtonElement>
	}
	errors?: ListOfErrors
	className?: string
	options: OptionType[]
}) {
	const [value, setValue] = React.useState(buttonProps.defaultValue?.toString())
	const [open, setOpen] = React.useState(false)
	const { key, name, ...selectProps } = buttonProps
	const fallbackId = useId()
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: buttonProps.defaultValue?.toString(),
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Select
				open={open}
				onOpenChange={setOpen}
				name={name}
				value={value}
				onValueChange={value => setValue(value)}
			>
				<SelectTrigger
					{...selectProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					onFocus={event => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={event => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				>
					<SelectValue placeholder={buttonProps.placeholder} />
				</SelectTrigger>
				<SelectContent>
					{options.map(({ label, value }) => (
						<SelectItem key={value} value={value}>
							{label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<div className="px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
